import { msg } from "@lingui/core/macro";
import { type Component, For, Show } from "solid-js";
import { UserRoundSearch } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";
import { useAccounts } from "@/features/auth/use-accounts";

/**
 * "You may be looking at this with the wrong account" — the account chooser for
 * a detail screen that resolved to NOT FOUND.
 *
 * Why this exists: a shared link now names only a resource, so opening someone
 * else's link lands on whatever account this tab happens to be showing. When
 * that account has no access, the honest answer is not a dead end — it is
 * "try one of your other accounts", exactly like Google Docs' account chooser.
 *
 * Local-first caveat (deliberate, documented): we do NOT probe each account for
 * access up front. Each account has its own PowerSync database, so answering
 * "who can see this?" would mean opening every one of them (or a server round
 * trip per account) before rendering an error screen. Instead we OFFER the
 * switch — one click, no navigation, the same URL re-resolving under the new
 * identity — which costs nothing when the guess is wrong.
 *
 * Renders nothing when there is no other account to offer, so the common
 * single-account case sees no extra chrome.
 */
export const TryAnotherAccount: Component = () => {
  const { t } = useI18n();
  const { accounts, activeUserId, switchHere } = useAccounts();

  const others = () => accounts().filter((account) => account.userId !== activeUserId());

  return (
    <Show when={others().length > 0}>
      <div class="mt-6 flex flex-col items-center gap-2" data-testid="try-another-account">
        <p class="text-xs text-muted-foreground">
          {t(msg`Signed in with more than one account? Try another one:`)}
        </p>
        <div class="flex flex-wrap items-center justify-center gap-2">
          <For each={others()}>
            {(account) => (
              <button
                type="button"
                onClick={() => {
                  void switchHere(account.userId);
                }}
                data-testid={`try-account-${account.userId}`}
                class="flex items-center gap-2 rounded-full border border-border/60 px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent/10 cursor-pointer"
              >
                <UserRoundSearch class="h-4 w-4 text-muted-foreground" />
                {account.kind === "guest" ? t(msg`Guest`) : account.displayName}
              </button>
            )}
          </For>
        </div>
      </div>
    </Show>
  );
};
